import Component from "../../Component";
import { BookingModel } from "../types";
import BaseApi from "./BaseApi";
import formatter from "../utils/formatter";

export default class BookingApi extends BaseApi {
    constructor(component: Component) {
        super(component);
    }

    public async fetchAllBookings(): Promise<BookingModel[]> {
        return this.fetchWithAuth<BookingModel[]>("/booking/");
    }

    public async fetchMyBookings(): Promise<BookingModel[]> {
        return this.fetchWithAuth<BookingModel[]>("/booking/me");
    }

    public async fetchBookingsForOthers(): Promise<BookingModel[]> {
        return this.fetchWithAuth<BookingModel[]>("/booking/forothers");
    }

    public async fetchBookingsWithFilter(
        employeeId?: string,
        startDate?: Date
    ): Promise<BookingModel[]> {
        const params = new URLSearchParams();

        if (employeeId) {
            params.append("employeeId", employeeId);
        }

        if (startDate) {
            // Use local datetime formatter to avoid UTC conversion issues
            params.append("startDate", formatter.formatLocalDateTime(startDate));
        }

        const queryString = params.toString();
        const endpoint = queryString ? `/booking/filter?${queryString}` : "/booking/filter";

        return this.fetchWithAuth<BookingModel[]>(endpoint);
    }

    public async fetchByIdAndDate(
        id: { seatId?: string } | { roomId: string },
        startDate?: string,
        endDate?: string
    ): Promise<BookingModel[]> {
        const params = new URLSearchParams();

        if ("seatId" in id && id.seatId) {
            params.append("seatId", id.seatId);
        }
        if ("roomId" in id && id.roomId) {
            params.append("roomId", id.roomId);
        }

        if (startDate) {
            params.append("startDate", startDate);
        }

        if (endDate) {
            params.append("endDate", endDate);
        }

        const queryString = params.toString();
        const endpoint = queryString ? `/booking/filter?${queryString}` : "/booking/filter";

        return this.fetchWithAuth<BookingModel[]>(endpoint);
    }

    public async fetchSelectedDate(startDate: string, endDate: string): Promise<BookingModel[]> {
        const params = new URLSearchParams();

        if (startDate) {
            params.append("startDate", startDate);
        }

        if (endDate) {
            params.append("endDate", endDate);
        }

        const endpoint = `/booking/timespan?startDate=${startDate}&endDate=${endDate}`;

        return this.fetchWithAuth<BookingModel[]>(endpoint);
    }

    public async cancelBooking(bookingId: string, cancelBookingDesc: string): Promise<void> {
        const body = {
            cancelDescription: cancelBookingDesc.trim() || "No reason provided for cancellation."
        };

        await this.putWithAuth<void>(`/booking/${bookingId}/cancel`, body);
    }

    public async createBooking(
        seatId: string,
        startTime: string,
        endTime: string,
        employeeId: string,
        eventId: string,
        description: string,
        attributes?: { AMOUNT_CHILDREN?: number; AMOUNT_DOGS?: number }
    ): Promise<void> {
        const bookingData = {
            startTime,
            endTime,
            employeeId,
            eventId,
            description,
            attributes
        };

        await this.postWithAuth<void>(`/booking/seat/${seatId}`, bookingData);
    }

    public async createRoomBooking(
        roomId: string,
        startTime: string,
        endTime: string,
        eventId: string,
        description: string,
        personAmount: number,
        employeeIds?: string[]
    ): Promise<void> {
        const bookingData: any = {
            startTime,
            endTime,
            eventId,
            description,
            personAmount
        };

        // Only include employeeIds if provided (not empty array or undefined)
        if (employeeIds && employeeIds.length > 0) {
            bookingData.employeeIds = employeeIds;
        }
        // If employeeIds is undefined or empty, don't include it in the payload

        await this.postWithAuth<void>(`/booking/room/${roomId}`, bookingData);
    }
    /**
     * Fetches availability between two time periods
     * @param startTime Start time in ISO format
     * @param endTime End time in ISO format
     * @returns Promise<any> Availability data
     */
    public async fetchAvailabilityBetween(startTime: string, endTime: string): Promise<any> {
        const endpoint = `/booking/between?startTime=${encodeURIComponent(startTime)}&endTime=${encodeURIComponent(endTime)}`;
        try {
            return await this.fetchWithAuth<any>(endpoint);
        } catch (error) {
            console.error("Error fetching availability between times:", error);
            return { seats: [] };
        }
    }

    /**
     * Fetches suggested employees based on previous bookings for others
     * @returns Promise<any[]> Array of suggested employees
     */
    public async fetchSuggestedEmployees(): Promise<any[]> {
        try {
            console.log("Fetching suggested employees...");
            // Fetch bookings made for others to get suggested employees
            const bookingsForOthers = await this.fetchBookingsForOthers();
            console.log("Bookings for others:", bookingsForOthers);

            if (!bookingsForOthers || bookingsForOthers.length === 0) {
                console.log(
                    "No previous bookings for others found - this is expected if you haven't booked for other people before"
                );
                // If no previous bookings for others, return empty array
                return [];
            }

            // Extract unique employees from previous bookings
            const uniqueEmployeesMap = new Map();

            bookingsForOthers.forEach((booking: any) => {
                if (booking.employee?.id) {
                    const employeeKey = booking.employee.id;
                    if (!uniqueEmployeesMap.has(employeeKey)) {
                        uniqueEmployeesMap.set(employeeKey, {
                            id: booking.employee.id,
                            firstName: booking.employee.firstName,
                            lastName: booking.employee.lastName,
                            department: booking.employee.department || null
                        });
                    }
                }
            });

            // Convert map to array and sort by last name
            const suggestedEmployees = Array.from(uniqueEmployeesMap.values());
            suggestedEmployees.sort((a, b) => {
                const lastNameComparison = (a.lastName || "").localeCompare(b.lastName || "");
                if (lastNameComparison !== 0) {
                    return lastNameComparison;
                }
                return (a.firstName || "").localeCompare(b.firstName || "");
            });

            console.log("Suggested employees found:", suggestedEmployees);
            return suggestedEmployees;
        } catch (error) {
            console.error("Error fetching suggested employees:", error);
            return [];
        }
    }
}
